import { hasAuthSession } from "utils/authSession";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { menuAction } from "ReduxReducers/menuReducer";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import themeRTL from "assets/theme/theme-rtl";
import { getColorMappings } from "components/ColorComponent/ColorComponent";
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import DraftSaveConfirmation from "components/InlineFeedback/DraftSaveConfirmation";
import { useMaterialUIController } from "context";
import { useEffect, useMemo, useState } from "react";
import useModalBackgroundLock from "hooks/useModalBackgroundLock";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import {
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import routes from "routes";
import { SocketProvider } from "socket/SocketContext";
import rtlPlugin from "stylis-plugin-rtl/dist/stylis-rtl";
import { appConstants } from "utils/appConstants";
import { getCityAndState } from "utils/generic";
import "./../src/App.scss";
import "./styles/theme-tokens.css";
import AppModal from "components/appModel/appmodal";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import HomePage from "layouts/Home/HomePage";
import brandDark from "assets/images/logo-ct-dark.png";
import brandWhite from "assets/images/logo-ct.png";
import Configurator from "examples/Configurator";
import Sidenav from "examples/Sidenav";
let GET_USER_DETAILS;
export default function App() {
  useModalBackgroundLock();
  const [controller] = useMaterialUIController();
  const reDispatch = useDispatch();
  const navigate = useNavigate();
  const {
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const isPublicAuthRoute =
    [
      "/login",
      "/customerLogin",
      "/reset-pass",
      "/set-new-password",
      "/authentication/sign-up",
      "/verify-email",
    ].includes(pathname) ||
    pathname.startsWith("/contact/") ||
    pathname.startsWith("/rcti-agreement/") ||
    pathname.startsWith("/rcti-received/") ||
    pathname.startsWith("/quotation/") ||
    pathname.startsWith("/invoice/") ||
    pathname.startsWith("/invoices/invoice/");
  const isStandaloneDashboard =
    pathname.startsWith("/creditNote/view/") ||
    pathname.startsWith("/debitNote/view/") ||
    pathname === "/rcti" ||
    pathname === "/rcti/add" ||
    pathname.startsWith("/rcti/edit/") ||
    pathname.startsWith("/rcti/view/") ||
    pathname.startsWith("/rctis/rcti/") ||
    pathname === "/rcti-agreements" ||
    pathname === "/dashboard" ||
    pathname === "/quotations" ||
    pathname === "/quotations/add" ||
    pathname.startsWith("/quotations/edit/") ||
    pathname.startsWith("/quotations/view/") ||
    pathname.startsWith("/quotations/quotation/") ||
    pathname === "/invoices" ||
    pathname === "/invoices/add" ||
    pathname.startsWith("/invoices/edit/") ||
    pathname.startsWith("/invoices/view/") ||
    pathname.startsWith("/invoices/invoice/");
  const countryState = useSelector((state) => state.countryState);
  const menu = useSelector((state) => state.menu.menuState);

  GET_USER_DETAILS = JSON.parse(sessionStorage.getItem("LOGIN_DETAILS"));
  const isChildUser = GET_USER_DETAILS?.userKey;

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // // Open sidenav when mouse enter on mini sidenav
  // const handleOnMouseEnter = () => {
  //   if (miniSidenav && !onMouseEnter) {
  //     setMiniSidenav(dispatch, false);
  //     setOnMouseEnter(true);
  //   }
  // };

  // // Close sidenav when mouse leave mini sidenav
  // const handleOnMouseLeave = () => {
  //   if (onMouseEnter) {
  //     setMiniSidenav(dispatch, true);
  //     setOnMouseEnter(false);
  //   }
  // };
  useEffect(() => {
    if (pathname === "/customerLogin" || pathname === "/login") {
      sessionStorage.removeItem("SIGN_AP_DETAILS");
    }
  }, [pathname]);

  useEffect(() => {
    if (!countryState.check) {
      getCityAndState();
    }
  }, [countryState.check]);

  let userDetails = JSON.parse(sessionStorage.getItem("LOGIN_DETAILS"));
  const roleName =
    sessionStorage.getItem("ROLE_NAME") ||
    JSON.parse(sessionStorage.getItem("ROLE") || "{}")?.roleName;
  const isSuperAdmin =
    roleName === "Super Admin" ||
    userDetails?.userType?.toLowerCase() === "admin";
  const isAuthenticated = hasAuthSession();
  const isPublicPath =
    pathname === "/" ||
    routes.some(
      (route) =>
        route.guarded === false &&
        route.route &&
        matchPath({ path: route.route, end: true }, pathname)
    );
  const authenticatedHome = isSuperAdmin
    ? "/dashboard-su"
    : roleName === "Warehouse Dashboard"
    ? "/warehouseDashboard"
    : "/dashboard";
  const loginRoutes = ["/login", "/customerLogin"];
  let logoName = "Invoice & Recover";
  if (!isSuperAdmin && userDetails) {
    const fullName = `${userDetails.firstName || ""} ${
      userDetails.lastName || ""
    }`.trim();
    if (fullName) logoName = fullName;
  }
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    const enforceProtectedRoute = () => {
      const hasActiveSession = hasAuthSession();
      if (!hasActiveSession && !isPublicPath)
        navigate("/login", { replace: true });
    };

    enforceProtectedRoute();
    window.addEventListener("pageshow", enforceProtectedRoute);
    window.addEventListener("popstate", enforceProtectedRoute);
    return () => {
      window.removeEventListener("pageshow", enforceProtectedRoute);
      window.removeEventListener("popstate", enforceProtectedRoute);
    };
  }, [isPublicPath, navigate]);

  const { tableBorder } = getColorMappings(sidenavColor);

  var checboxMain = document.querySelectorAll(".MuiCheckbox-root");
  for (let i = 0; i < checboxMain.length; i++) {
    var use = checboxMain[i].getElementsByTagName("svg");
    if (darkMode) {
      use[0].style.borderColor = "white";
    } else {
      use[0].style.borderColor = "black";
    }
  }
  var checkboxMain = document.querySelectorAll(".MuiRadio-root");
  for (let i = 0; i < checkboxMain.length; i++) {
    let use = checkboxMain[i].getElementsByTagName("svg");
    if (darkMode) {
      use[0].style.borderColor = "white";
    } else {
      use[0].style.borderColor = "black";
    }
  }
  // var dropicons = document.querySelectorAll('.MuiInputBase-root');
  // for (let i = 0; i < dropicons.length; i++) {
  //   var svgicon = dropicons[i].getElementsByTagName('svg');
  //   if (svgicon.length > 0) {
  //     if (darkMode) {
  //       svgicon[0].setAttribute("style", "fill: white !important;");
  //     } else {
  //       svgicon[0].setAttribute("style", "fill: ");
  //     }
  //   }
  // }
  var dropicons = document.querySelectorAll(".MuiInputBase-root");
  for (let i = 0; i < dropicons.length; i++) {
    var svgicon = dropicons[i].getElementsByTagName("svg");
    if (svgicon.length > 0) {
      if (darkMode) {
        svgicon[0].setAttribute(
          "style",
          "fill: white !important; opacity: 0.7; !important"
        );
      } else {
        svgicon[0].setAttribute("style", "fill:");
      }
    }
  }

  let noRecord = document.getElementsByClassName("rdt_Table")[0];

  if (noRecord) {
    noRecord.getElementsByTagName("div")[0].style.fontSize = "15px";
  }

  let tabBorder = document.getElementsByClassName("tab-container");

  if (tabBorder) {
    for (let i = 0; i < tabBorder.length; i++) {
      tabBorder[i].style.borderColor = tableBorder;
    }
  }

  // var addNewButton = document.getElementsByClassName('btn-block');
  const getRoutesGuarded = (allRoutes, val) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutesGuarded(route.collapse, val);
      }
      if (val) {
        if (route.route) {
          const element = loginRoutes.includes(route.route) ? (
            <Navigate to={authenticatedHome} replace />
          ) : route.guarded &&
            route.permission === "None" &&
            route.route !== "/dashboard" ? (
            <Navigate to={authenticatedHome} replace />
          ) : (
            route.component
          );
          return (
            <Route
              exact
              path={route.route}
              element={element}
              key={route.key}
              title={route.title}
            />
          );
        }
      } else {
        if (route.guarded === val) {
          if (route.route) {
            return (
              <Route
                exact
                path={route.route}
                element={route.component}
                key={route.key}
                title={route.title}
              />
            );
          }
        }
      }
      return null;
    });
  const handleRoute = async (id) => {
    reDispatch(menuAction(false));
    navigate("/subscriptions");
  };

  const handleCancel = (name) => (event) => {
    reDispatch(menuAction(false));
  };
  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        <DraftSaveConfirmation />
        {layout === "dashboard" &&
          userDetails &&
          !isPublicAuthRoute &&
          (isSuperAdmin ? (
            <>
              <Sidenav
                color={sidenavColor}
                brand={
                  (transparentSidenav && !darkMode) || whiteSidenav
                    ? brandDark
                    : brandWhite
                }
                brandName="Invoice & Recover"
                routes={routes}
              />
              <Configurator />
            </>
          ) : (
            !isStandaloneDashboard && (
              <DashboardNavigation
                organisation={
                  userDetails.companyName ||
                  userDetails.businessName ||
                  userDetails.organisationName ||
                  userDetails.organizationName ||
                  userDetails.fullName ||
                  userDetails.name ||
                  `${userDetails.firstName || ""} ${
                    userDetails.lastName || ""
                  }`.trim() ||
                  "Invoice & Recover"
                }
                role={userDetails.roleName || userDetails.userType || "User"}
              />
            )
          ))}
        {layout === "vr" && <Configurator />}
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to={authenticatedHome} replace />
              ) : (
                <HomePage />
              )
            }
          />
          {getRoutesGuarded(routes, isAuthenticated)}
          <Route
            path="*"
            element={
              <Navigate
                to={isAuthenticated ? authenticatedHome : "/login"}
                replace
              />
            }
          />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <DraftSaveConfirmation />
      {layout === "dashboard" &&
        userDetails &&
        !isPublicAuthRoute &&
        (isSuperAdmin ? (
          <>
            <Sidenav
              color={sidenavColor}
              brand={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              brandName={logoName}
              routes={routes}
            />
            <Configurator />
          </>
        ) : (
          !isStandaloneDashboard && (
            <DashboardNavigation
              organisation={
                userDetails.companyName ||
                userDetails.businessName ||
                userDetails.organisationName ||
                userDetails.organizationName ||
                userDetails.fullName ||
                userDetails.name ||
                `${userDetails.firstName || ""} ${
                  userDetails.lastName || ""
                }`.trim() ||
                "Invoice & Recover"
              }
              role={userDetails.roleName || userDetails.userType || "User"}
            />
          )
        ))}
      {layout === "vr" && <Configurator />}
      <SocketProvider socketUrl={appConstants.socketUrl}>
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to={authenticatedHome} replace />
              ) : (
                <HomePage />
              )
            }
          />
          {getRoutesGuarded(routes, isAuthenticated)}
          <Route
            path="*"
            element={
              <Navigate
                to={isAuthenticated ? authenticatedHome : "/login"}
                replace
              />
            }
          />
        </Routes>
      </SocketProvider>
      {!isChildUser && isChildUser !== "ChildUser" ? (
        <DeleteConfirmationDialog
          open={menu}
          handleDelete={handleRoute}
          message={
            <span style={{ textAlign: "center", display: "block" }}>
              "This Screen is not available in current plan. Please upgrade the
              plan to use this screen."
            </span>
          }
          handleCancel={handleCancel("handleCancel")}
          closeModalClick={handleCancel("closeModalClick")}
        />
      ) : (
        <AppModal
          open={menu}
          title="Access restricted"
          message="You don’t have permission to access this screen. Contact your administrator if you need access."
          handleCancel={handleCancel("handleCancel")}
          closeModalClick={handleCancel("closeModalClick")}
        />
      )}
    </ThemeProvider>
  );
}
